import { html, css, nothing, LitElement } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import { map } from 'lit/directives/map.js';
import { connect } from 'pwa-helpers/connect-mixin.js';
import { store } from '../../store/store.js';
import sharedStyles from '../../app/styles.js';
import { saveScormItem, updateScormItem, addScormPackage } from '../../store/scorm/scorm.js';
import { navigateBack } from '../../store/route/route.js';
import { previewElearning } from '../../store/preview/preview.js';
import { translate } from '../../helpers/translate.js';
import { formatDateTime } from '../../helpers/controls.js';
import { createSupplierKey, launchTypes } from '../../store/elearning/helpers.js';
import '@sdb-develop/learning-frontend';
import '@vaadin/select/theme/material/vaadin-select.js';
import '@polymer/paper-input/paper-input.js';
import '@polymer/iron-form/iron-form.js';
import '../shared/sdb-loading.js';
import '../shared/sdb-overlay-layout.js';
import './sdb-scorm-package-upload.js';

@customElement('sdb-scorm-item')
class SdbScormItem extends connect(store)(LitElement) {

    @property({ type: String })
    accessor targetId;

    @state()
    accessor scormItem;

    @state()
    accessor isLoading = false;

    @state()
    accessor isDirty = false;

    @query('#scorm_item_form')
    accessor scormItemForm;

    @query('#scorm_uploader')
    accessor scormUploader;

    get latestVersion() {
        if (!this.scormItem.versions?.length)
            return null;
        return this.scormItem.versions[this.scormItem.versions.length-1];
    }

    stateChanged(state) {
        this.scormItem = state.scorm?.current;
        this.isLoading = state.scorm?.status === 'loading' || !state.scorm?.current;
        this.isDirty = state.scorm?.dirty;
        this.settings = state.env.settings;
        this.user = state.auth.user;
        this.portalId = state.env.portalIdentifier;
    }

    static get styles() {
        return [
            sharedStyles,
            css`
                [slot=content] {
                    width: 100%;
                    padding: 0 20px;
                    max-width: 1400px;
                }

                sdb-content-card {
                    max-width: 1400px;
                    margin-bottom: 16px;
                }

                sdb-content-card .headline {
                    display: inline-flex;
                    align-items: center;
                    color: var(--text-primary);
                    padding: 10px 0 20px 0;
                }

                sdb-content-card .headline span {
                    margin-left: 24px;
                    font-size: 16px;
                    font-family: var(--font-family);
                }

                paper-input {
                    margin-bottom: 5px;
                }

                paper-input[required] label {
                    color: red;
                }

                vaadin-select {
                    width: 30%;
                    min-width: 200px;
                }

                .uploader_container {
                    margin-top: 30px;
                }

                .uploader_container > span {
                    display: block;
                    font-size: 14px;
                    margin-bottom: 10px;
                    margin-left: 25px;
                }
            `,
        ];
    }

    render() {
        if (this.isLoading || this.targetId !== this.scormItem.targetId) {
            return html`<sdb-loading></sdb-loading>`;
        }

        const update = (changes) => store.dispatch(updateScormItem({ changes }));

        return html`
            <sdb-overlay-layout
                headline=${this.scormItem.title}
                ?dirty=${this.isDirty}
                @back=${() => { store.dispatch(navigateBack('scorm')); }}>

                <div slot="buttons">
                    <sdb-content-button
                        id="preview_button"
                        label=${translate('preview')}
                        primary
                        ?disabled=${this.isDirty}
                        @click=${()=> { store.dispatch(previewElearning({ item: this.scormItem })); }}>
                    </sdb-content-button>
                    <sdb-content-button
                        id="save_button"
                        label=${translate('save')}
                        primary
                        ?disabled=${!this.isDirty}
                        @click=${() => {
                            if (this.scormItemForm.validate())
                                store.dispatch(saveScormItem({ id: this.scormItem.id }));
                        }}>
                    </sdb-content-button>
                </div>

                <iron-form id="scorm_item_form" slot="content">
                    <form>
                        <sdb-layout-100>

                            <sdb-content-card id="general_card">
                                <paper-input
                                    id="name_input"
                                    transparent
                                    label=${translate('name')}
                                    value=${this.scormItem.title}
                                    @change=${(e) => update({ title: e.target.value })}>
                                </paper-input>

                                <paper-input
                                    id="description_textarea"
                                    transparent
                                    label=${translate('description')}
                                    value=${this.scormItem.description}
                                    @change=${(e) => update({ description: e.target.value })}>
                                </paper-input>

                                <paper-input
                                    id="supplier_input"
                                    transparent
                                    label=${translate('supplier')}
                                    value=${this.scormItem.supplier.name}
                                    @change=${(e) => update({
                                        supplier: {
                                            name: e.target.value,
                                            supplierKey: createSupplierKey(e.target.value),
                                        }
                                    })}>
                                </paper-input>

                                <vaadin-select
                                    id="launchtype_select"
                                    transparent
                                    label=${translate('launchType')}
                                    value=${String(this.scormItem.launchType)}
                                    @change=${(e) => { update({ launchType: parseInt(e.target.value, 10) }) }}
                                    .items=${Object.entries(launchTypes).map(([key, value]) => ({
                                        label: translate(key.toLowerCase()),
                                        value: String(value),
                                    }))}>
                                </vaadin-select>

                            </sdb-content-card>

                            ${this.latestVersion ? html`
                                <sdb-content-card id="versions_card">
                                    <div class="headline">
                                        <sdb-content-icon
                                            icon="fal fa-file-zip"
                                            size="32">
                                        </sdb-content-icon>
                                        <span>SCORM Package<span>
                                    </div>

                                    <sdb-content-accordion>
                                        <sdb-content-accordionitem
                                            overline=${translate('latestVersion')}
                                            headline=${this.latestVersion.startData?.name}
                                            subtitle=${formatDateTime(this.latestVersion.startData?.timestamp)}>
                                            <sdb-content-list class="scroller">
                                                <sdb-content-listheading>
                                                    <sdb-content-headingitem
                                                        textDark
                                                        slot="1"
                                                        headline=${translate('version')}>
                                                    </sdb-content-headingitem>
                                                    <sdb-content-headingitem
                                                        textDark
                                                        slot="2"
                                                        headline=${translate('package')}>
                                                    </sdb-content-headingitem>
                                                    <sdb-content-headingitem
                                                        textDark
                                                        slot="3"
                                                        headline=${translate('uploaded')}>
                                                    </sdb-content-headingitem>
                                                </sdb-content-listheading>
                                                ${map(this._reverse(this.scormItem.versions), (version) => html`
                                                    <sdb-content-listitem
                                                        headline=${version.version}
                                                        noPadding
                                                        noHover>
                                                        <span slot="2">${version.startData?.name}</span>
                                                        <span slot="3">${formatDateTime(version.startData?.timestamp)}</span>
                                                    </sdb-content-listitem>
                                                `)}
                                                <sdb-content-listitem
                                                    noPadding
                                                    noHover>
                                                    <div class="uploader_container">
                                                        <span>${translate('uploadNewVersion')}</span>
                                                        ${this._renderUploader()}
                                                    </div>
                                                </sdb-content-listitem>
                                            </sdb-content-list>
                                        </sdb-content-accordionitem>
                                    </sdb-content-accordion>

                                </sdb-content-card>
                            ` : html`
                                <sdb-content-card id="upload_card">
                                    <div class="headline">
                                        <sdb-content-icon
                                            icon="fal fa-file-import"
                                            size="32">
                                        </sdb-content-icon>
                                        <span>${translate('uploadNewPackage')}<span>
                                    </div>

                                    ${this._renderUploader()}
                                </sdb-content-card>
                            `}

                        </sdb-layout-100>
                    </form>
                </iron-form>
            </sdb-overlay-layout>
        `;
    }

    firstUpdated() {
        this.dispatchEvent(
            new CustomEvent('fullscreen', {
                bubbles: true,
                composed: true,
                detail: {
                    fullscreen: true,
                },
            }),
        );
    }

    _renderUploader() {
        return html`
            <sdb-scorm-package-upload
                id="scorm_uploader"
                @upload-completed=${(e) => {
                    store.dispatch(addScormPackage({ scormPackage: e.detail.package }))
                    this.scormUploader.clear();
                }}>
            </sdb-scorm-package-upload>
        `;
    }

    _reverse(items) {
        return [...items].reverse();

    }
}
